import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Link,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import httpClient from '../services/httpClient';
import { authPath, persistRedirectFromSearch, postLoginPath } from '../utils/authRedirect';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for session expired error in URL (from hub-gateway redirect)
    const params = new URLSearchParams(location.search);
    persistRedirectFromSearch(location.search);
    if (params.get('error') === 'session_expired') {
      setSessionError('Your session has expired. Please log in again.');
      params.delete('error');
      const qs = params.toString();
      window.history.replaceState({}, document.title, qs ? `/login?${qs}` : '/login');
    }
    
    // Check for authError in localStorage (from httpClient interceptor)
    const authError = localStorage.getItem('authError');
    if (authError) {
      setSessionError(authError);
      localStorage.removeItem('authError');
    }
    
    // Check if email is enabled for password reset
    const checkEmailStatus = async () => {
      try {
        const response = await httpClient.get('/v1/auth/registration-info');
        setEmailEnabled(response.data.emailEnabled || false);
      } catch (err) {
        console.error('Failed to check email status:', err);
      }
    };
    checkEmailStatus();
  }, [location.search]);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(credentials);
    
    if (result.success) {
      navigate(postLoginPath(location.search), { replace: true });
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavBar />
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              EyeSense.ai Hub
            </Typography>
            <Typography variant="subtitle1" align="center" color="textSecondary" gutterBottom>
              Central Management Portal
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ✓ Have you installed your Gateway Software?
              </Typography>
              <Typography variant="caption">
                You'll need to have deployed at least one EyeSense.ai Gateway before you can manage it via this hub interface. If you haven't installed your gateway yet, please{' '}
                <Link
                  component="button"
                  variant="inherit"
                  onClick={() => navigate('/install-gateway')}
                  sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                >
                  follow our installation guide
                </Link>
                {' '}after logging in.
              </Typography>
            </Alert>
            
            {sessionError && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {sessionError}
              </Alert>
            )}
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={credentials.username}
                onChange={handleChange}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={credentials.password}
                onChange={handleChange}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
              
              {emailEnabled && (
                <Box textAlign="center" mt={1}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/forgot-password')}
                    type="button"
                    sx={{ textDecoration: 'none' }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              )}
              
              <Box textAlign="center" mt={2}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate(authPath('/register', location.search))}
                  sx={{ textDecoration: 'none' }}
                >
                  Don't have an account? Create one
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
